import React from "react";
import { MessageCircle } from "lucide-react";
import { Reveal, Eyebrow } from "./Decorative";
import { INTEREST_ANCHOR, INTEREST_BLURB, WHATSAPP_URL } from "../lib/constants";
import { check } from "../lib/forms";
import { trackInterestSubmit, trackWhatsAppClick } from "../lib/track";
import { useSiteForm, SiteForm, Field, Submit, SendError, ThankYou, RequiredNote } from "./forms/FormKit";

/**
 * Form A — "Bring Antara to your city". Demand capture for the next editions,
 * and the target of every "Tell us your city" / "Keep me posted" link on the
 * site (the /#interest anchor). Under 30 seconds: three short required
 * fields, three optional ones.
 *
 * Posts to the Netlify form "interest" — see lib/forms.js for the contract.
 *
 * `tone` = "light" (default, on cream/ivory) or "dark" (on #0F0A07).
 */
const RULES = {
  name: [check.required("name")],
  email: [check.email],
  phone: [check.phone({ optional: true })],
  city: [check.required("city")],
  country: [check.required("country")],
};

export default function InterestForm({ tone = "light", className = "", heading, blurb }) {
  const dark = tone === "dark";
  const form = useSiteForm("interest", {
    initial: { country: "India" },
    rules: RULES,
    onSent: (v) => trackInterestSubmit(v.city),
  });

  if (form.status === "done") {
    return (
      <section id={INTEREST_ANCHOR} className={`scroll-mt-24 ${className}`}>
        <ThankYou form={form} tone={tone} title="You're on the list.">
          We'll write to you when Antara has a date near {form.values.city.trim() || "you"}. No newsletter,
          no noise — just the next edition.
        </ThankYou>
      </section>
    );
  }

  return (
    <section id={INTEREST_ANCHOR} className={`scroll-mt-24 ${className}`} data-testid="interest-section">
      <div className="max-w-xl mx-auto">
        <Reveal>
          <div className="text-center">
            <Eyebrow>{dark ? <span style={{ color: "#C9A961" }}>Next editions</span> : "Next editions"}</Eyebrow>
            <h2
              className="mt-4 font-display text-3xl md:text-4xl leading-tight"
              style={{ color: dark ? "#FBF6EE" : undefined }}
            >
              {heading || (
                <>
                  Bring Antara to <span className="gold-grad italic">your city</span>
                </>
              )}
            </h2>
            <p
              className="mt-4 font-body text-base leading-relaxed"
              style={{ color: dark ? "#E8D9C5" : undefined }}
            >
              {blurb || INTEREST_BLURB}
            </p>
          </div>
        </Reveal>

        <SiteForm form={form} tone={tone} className="mt-9 space-y-5 text-left" testid="interest-form">
          <RequiredNote />
          <Field form={form} name="name" label="Name" required autoComplete="name" placeholder="Your name" />
          <Field
            form={form} name="email" label="Email" required
            type="email" inputMode="email" autoComplete="email" autoCapitalize="off" spellCheck={false}
            placeholder="you@example.com"
          />
          <div className="grid grid-cols-1 min-[420px]:grid-cols-2 gap-5">
            <Field form={form} name="city" label="City" required autoComplete="address-level2" placeholder="e.g. Jaipur" />
            <Field form={form} name="country" label="Country" required autoComplete="country-name" />
          </div>
          <Field
            form={form} name="phone" label="Phone" optional
            type="tel" inputMode="tel" autoComplete="tel" placeholder="+91 98765 43210"
            hint="Only if you'd like a WhatsApp when tickets open."
          />
          <Field
            form={form} name="drewYou" label="What drew you to Antara?" optional
            maxLength={200} placeholder="A reel, a friend, the bhajans…"
          />

          <SendError form={form} />
          <Submit form={form}>Keep me posted</Submit>

          <p
            className="text-center font-body text-xs leading-relaxed"
            style={{ color: dark ? "rgba(232,217,197,0.65)" : undefined }}
          >
            One email when there's a date near you. Nothing else, and you can leave any time.
          </p>
        </SiteForm>

        <p className="mt-6 text-center">
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={trackWhatsAppClick}
            className={`inline-flex items-center gap-1.5 font-body text-sm hover:opacity-80 transition ${
              dark ? "text-[#E5C880]" : "text-amber"
            }`}
            data-testid="interest-whatsapp-link"
          >
            <MessageCircle size={14} /> A question? WhatsApp
          </a>
        </p>
      </div>
    </section>
  );
}
